import { Span, Text, Tooltip, TooltipTrigger, VisuallyHidden } from "@hopper-ui/components";
import { type ReactNode, type Ref, useRef, useState } from "react";
import { NavLink, type NavLinkProps } from "react-router";
import { useTrackLinkClicked } from "../../../tracking/useTrackEvent.ts";
import styles from "./SidebarLink.module.css";

const TRACKING_LOCATION = "Sidebar";

interface SidebarLinkProps extends NavLinkProps {
    icon: ReactNode;
    badge?: ReactNode;
    size?: "expanded" | "collapsed";
    children: ReactNode;
}

export function SidebarLink(props: SidebarLinkProps) {
    if (props.size === "collapsed") {
        return <CollapsedSidebarLink {...props} />;
    }

    return <SidebarLinkAnchor {...props} />;
}

// Mounted only while collapsed, so the tooltip's open state resets on every collapse/expand toggle.
function CollapsedSidebarLink(props: SidebarLinkProps) {
    const linkRef = useRef<HTMLAnchorElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    // react-aria positions the tooltip once, off the trigger's layout box at open time. Refuse to open
    // if that box isn't there yet, or the tooltip gets stuck at the viewport origin with no way to dismiss it.
    const handleOpenChange = (open: boolean) => {
        const element = linkRef.current;

        if (open && !(element?.isConnected && element.getClientRects().length > 0)) {
            return;
        }

        setIsOpen(open);
    };

    return (
        <TooltipTrigger placement="right" isOpen={isOpen} onOpenChange={handleOpenChange}>
            <SidebarLinkAnchor {...props} ref={linkRef} />
            <Tooltip>{props.children}</Tooltip>
        </TooltipTrigger>
    );
}

interface SidebarLinkAnchorProps extends SidebarLinkProps {
    ref?: Ref<HTMLAnchorElement>;
}

function SidebarLinkAnchor(props: SidebarLinkAnchorProps) {
    const { children, to, icon, badge, size = "expanded", ref, onClick, ...rest } = props;
    const trackLinkClicked = useTrackLinkClicked();

    const handleClick: SidebarLinkAnchorProps["onClick"] = event => {
        trackLinkClicked(typeof children === "string" ? children : "", TRACKING_LOCATION);
        onClick?.(event);
    };

    return (
        <NavLink
            ref={ref}
            className={({ isActive }) =>
                [
                    styles["sidebarLink"],
                    size === "collapsed" ? styles["collapsed"] : null,
                    isActive ? styles["active"] : null
                ]
                    .filter(Boolean)
                    .join(" ")
            }
            to={to}
            onClick={handleClick}
            {...rest}
        >
            <Span className={styles["sidebarIcon"]}>{icon}</Span>
            {size === "expanded" ? (
                <Text size="sm" fontWeight="body-sm-medium" data-public className={styles["sidebarTitle"]}>
                    {children}
                </Text>
            ) : (
                <VisuallyHidden>{children}</VisuallyHidden>
            )}
            {size === "expanded" && badge}
        </NavLink>
    );
}
